import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  TextField,
  TablePagination,
  Button,
  Box,
  InputAdornment,
  Modal,
  CircularProgress,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import axios from "axios";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const statusColors = {
  REVIEW: "#FFCD82",
  UPCOMING: "#54A7FF",
  REJECTED: "#FA6459",
  REWORK: "#EA981F",
  ACCEPTING_BIDS: "#009688",
  BIDS_CLOSED: "#BEB3B3",
  REBIDDING: "#FF9800",
  AWARDED: "#0381F3",
  DISCARDED: "#CB856B",
};

const Tenders = ({ tenders, isLoading, getTenders }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const handleOpen = (s) => {
    setOpen(true);
    setStatus(s);
  };

  const handleClose = () => {
    setOpen(false);
    setStatus("");
    setReason("");
  };

  const handleSelectAllClick = (event, data) => {
    const ids = data.map((t) => t.tenderId);
    if (event.target.checked) {
      setSelected(Array.from(new Set([...selected, ...ids])));
    } else {
      setSelected((prev) => prev.filter((id) => !ids.includes(id)));
    }
  };

  const handleClick = (tenderId) => {
    setSelected((prevSelected) =>
      prevSelected.includes(tenderId)
        ? prevSelected.filter((id) => id !== tenderId)
        : [...prevSelected, tenderId]
    );
  };

  const paginatedTenders = useMemo(() => {
    const filtered = tenders?.filter((t) =>
      t.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return filtered?.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [tenders, searchTerm, page]);

  const handleTenderUpdate = async () => {
    try {
      const payload = selected.map((tenderId) => ({
        tenderId,
        status,
        username: storedUser.username,
        reason: reason,
      }));

      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/tender`, payload, {
        headers: { Authorization: `Bearer ${storedUser.accessToken}` },
      });

      setSelected([]);
      setOpen(false);
      getTenders();
    } catch (error) {
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const [date, time] = dateString.split("T");
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year} ${time}`;
  };
  return (
    <Paper
      sx={{ width: "100%", overflow: "hidden", boxShadow: "none !important" }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Button className="accept-all" onClick={() => handleOpen("UPCOMING")}>
            Approve Selected
          </Button>
          <Button className="reject-all" onClick={() => handleOpen("REJECTED")}>
            Reject Selected
          </Button>
        </Box>
        <Box display="flex" alignItems="center" gap={1} p={2}>
          <TextField
            placeholder="Search"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              "&:hover": { background: "#D8E9F0" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* <Button
            startIcon={<FilterListIcon />}
            sx={{
              height: 40,
              border: "1px solid #c7c7c7 !important",
              borderRadius: "12px",
              color: "#000",
              textTransform: "none",
            }}
            onClick={() => console.log("Filter clicked")}
          >
            Filter
          </Button> */}
        </Box>
      </Box>

      <TableContainer sx={{ fontFamily: "Inter !important" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selected.length > 0 &&
                    selected.length < paginatedTenders.length
                  }
                  checked={
                    paginatedTenders?.length > 0 &&
                    selected.length === paginatedTenders.length
                  }
                  onChange={(event) =>
                    handleSelectAllClick(event, paginatedTenders)
                  }
                />
              </TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Tender Type</TableCell>
              <TableCell>Tender Publish Date</TableCell>
              <TableCell>Bid Close Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ fontFamily: "Inter !important" }}>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : paginatedTenders?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No rows
                </TableCell>
              </TableRow>
            ) : (
              paginatedTenders?.map((row, index) => {
                const tenderId = row.tenderId;
                const isItemSelected = selected.includes(tenderId);
                return (
                  <TableRow
                    key={row.tenderId}
                    hover
                    role="checkbox"
                    selected={isItemSelected}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#EAF4FF" : "#ffffff",
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={() => handleClick(tenderId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Link to={`/dashboard/${row.tenderId}`}>
                        <span className="table-title">{row.title}</span>
                      </Link>
                    </TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{formatDate(row.tenderPublishDate)}</TableCell>
                    <TableCell>
                      {formatDateTime(row.bidSubmissionEndDate)}
                    </TableCell>
                    <TableCell>
                      <span
                        style={{
                          background: statusColors[row.status],
                          borderRadius: "15px",
                          padding: "5px 15px",
                          fontWeight: 600,
                        }}
                      >
                        {row.status.replace("_", " ")}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Link to={`/dashboard/tender/${row.tenderId}`} onClick={() => localStorage.setItem("action", "pending")}>View</Link>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={tenders?.length || 0}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
      />

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography variant="h5" fontWeight="bold">
            {status === "DISQUALIFIED"
              ? "Reject the Tender*"
              : "Accept the Tender*"}
          </Typography>
          <label htmlFor="reason">
            Please give reason for{" "}
            {status === "DISQUALIFIED" ? "Reject" : "Accept"}ing the tender*
          </label>
          <textarea
            name="reason"
            id="reason"
            onChange={(e) => setReason(e.target.value)}
            rows={8}
            style={{ width: "100%", marginTop: "10px" }}
          ></textarea>
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <Button className="btn-outlined" onClick={handleClose}>
              Close
            </Button>
            <Button
              className={
                status === "REJECTED"
                  ? "btn-outlined btn-reject"
                  : "btn-contained"
              }
              color={status === "REJECTED" ? "error" : "primary"}
              onClick={handleTenderUpdate}
            >
              {status === "REJECTED" ? "Reject" : "Accept"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default Tenders;
